import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import { GetLensesType, MyLensesSortDirection, SortBy } from "../../gql-generated/types";
import {
    type GetTopLensesInfoQuery,
    type GetTopLensesInfoQueryVariables,
    getTopLensesInfo,
} from "../../lens-insights/api/queries";
import { type ApolloResult } from "../../shared/models";
import { type SpotlightStoriesQuery, spotlightStoriesQuery } from "../api/queries";

export interface SpotlightStoryWithLensInfo {
    storySnapId: string;
    unlockableId: string;
    views: number;
    mediaUrl: string;
    lensName?: string;
    lensIconUrl?: string;
    lensId?: string;
    orgId?: string;
    lensFolderId?: string;
}

type UseSpotlightStoriesReturn = ApolloResult<{
    stories: readonly SpotlightStoryWithLensInfo[];
}>;

export const useSpotlightStories = (): UseSpotlightStoriesReturn => {
    const { data, loading: storiesLoading, error } = useQuery<SpotlightStoriesQuery>(spotlightStoriesQuery);

    const unlockableIdList = useMemo(() => {
        const stories = data?.spotlightStories.stories ?? [];
        return [...new Set(stories.map((s) => s.unlockableId))];
    }, [data?.spotlightStories.stories]);

    const { data: lensInfoData } = useQuery<GetTopLensesInfoQuery, GetTopLensesInfoQueryVariables>(getTopLensesInfo, {
        variables: {
            filterBy: {
                unlockableIdList,
                lockedList: [],
                nameList: [],
                statusList: [],
                surfaceTypeList: [],
            },
            type: GetLensesType.Community,
            limit: unlockableIdList.length,
            offset: 0,
            sortBy: SortBy.SortByUnset,
            sortDirection: MyLensesSortDirection.SortDirectionUnset,
        },
        skip: unlockableIdList.length === 0,
    });

    const stories = useMemo<readonly SpotlightStoryWithLensInfo[]>(() => {
        const rawStories = data?.spotlightStories.stories ?? [];
        const lensesList = lensInfoData?.lenses.lensesList;

        const lensMap = new Map(lensesList?.map((l) => [l.unlockableId.toString(), l]) ?? []);

        return rawStories.map((story) => {
            const lens = lensMap.get(story.unlockableId);
            return {
                storySnapId: story.storySnapId,
                unlockableId: story.unlockableId,
                views: story.views,
                mediaUrl: story.mediaUrl,
                lensName: lens?.name,
                lensIconUrl: lens?.iconUrl,
                lensId: lens?.id,
                orgId: lens?.organizationId,
                lensFolderId: lens?.lensAccountId?.lensSpaceId ?? undefined,
            };
        });
    }, [data?.spotlightStories.stories, lensInfoData?.lenses.lensesList]);

    return useMemo(
        () => ({
            // Lens info is non-blocking — stories render without overlay if it fails
            loading: storiesLoading,
            error,
            stories,
        }),
        [stories, storiesLoading, error],
    );
};
